/**
 * variant-practice — 「出變形練習卷」:用一張剛上傳/批改的考卷上的**每一題**
 * (答對的也算),生一題「同概念、不同數字/情境」的變形題,讓小孩練概念而不是
 * 死背原題。
 *
 * 緣由(boss 2026-06-10):現有複習卷只對「答錯」的題出變形;答對的就被忽略、浪費。
 * 老闆要求答對也要能出變形題。
 *
 * 變形來源:
 *   - 有圖表的題(統計表/長條圖…)→ LLM 帶著原圖表資料,生「換過數字的新圖表 +
 *     對應新問題」(generateSimilarQuestion 會誤用數學 generator 出成加法題,故另走)。
 *   - 其他題 → 沿用 generateSimilarQuestion(數學 generator / 其他科 LLM)。
 *   - 任何一題生不出來 → 略過(優雅降級),不阻斷整卷。
 *
 * 視覺 = 學術風格(pageShell/ef token)。學習/遊戲分離:答錯回流錯題本,只動學習側。
 */

import type { ExtractedItem } from '../exam-upload/ocr-client';
import type { Figure } from '../exam-upload/figure';
import { normalizeFigure, renderFigure } from '../exam-upload/figure';
import { generateSimilarQuestion, normalizeAnswer } from '../mistake-similar';
import { textComplete } from '../text-complete-client';
import { coerceJsonObject, coerceJsonArray } from '../llm-json';
import { getMathGenerator } from '../generators/math';
import { loadProgress, saveProgress } from '../../shared/progress';
import { recordMistake, deriveQuestionId } from '../../shared/mistake-log';
import { findOrCreateKP, recordAttemptV2 } from '../../shared/student-kp';
import type { Subject } from '../../shared/types';
import { pageShell, academicButton } from '../ui/components';
import { feedMistakesToWiki } from './wiki-bridge';

/** 家長批改結果(挑重點題時答錯/粗心優先)。 */
export type VariantDecision = 'correct' | 'wrong' | 'careless';

/** 變形卷的來源題:考卷題 + 可選的批改結果(有 decision 挑重點才挑得準)。 */
export interface VariantSourceItem extends ExtractedItem {
  decision?: VariantDecision;
}

/** 題數超過這個門檻才提供「重點短卷 / 整卷變形」選擇;以下直接整卷。 */
export const KEY_PICK_THRESHOLD = 6;

/** 一題變形練習題(填答 + 即時對錯 + 可選圖表)。 */
export interface VariantItem {
  subject: Subject;
  kp: string;
  prompt: string;
  displayAnswer: string;
  acceptedAnswers: string[];
  explanation: string;
  /** 變形後重繪的圖表(原題有圖才有)。 */
  figure?: Figure;
}

/** 一題有圖表 → 用 LLM 生「換數字的新圖表 + 新問題」。生不出回 null。 */
async function generateFigureVariant(item: ExtractedItem): Promise<VariantItem | null> {
  const figure = item.figure;
  if (!figure) return null;
  const system =
    '你是台灣國小三年級的出題老師。只用繁體中文。只輸出單一 JSON 物件,不要任何多餘文字或 markdown。';
  const prompt =
    '下面是一題「看圖表回答」的題目,附了圖表的結構化資料。請出一題「同類型、同難度」' +
    '的新題:把圖表裡的數字換成合理的新數字(小三範圍),問法類似但答案要依新數字算。\n' +
    `原題:${item.prompt}\n` +
    `原圖表(JSON):${JSON.stringify(figure)}\n\n` +
    '用這個 JSON 格式回覆(只回 JSON,figure 要跟原圖表同樣的 type 與欄位結構):\n' +
    '{"prompt":"新題目文字","figure":<新圖表物件>,"answer":"標準答案(簡短)","explanation":"一句怎麼算"}';

  const raw = await textComplete({ system, prompt });
  if (!raw) return null;
  const o = coerceJsonObject(raw); // 容錯 fence/陣列/夾雜文字(2026-06-11「生不出考卷」根因之一)
  if (!o) return null;
  const p = typeof o.prompt === 'string' ? o.prompt.trim() : '';
  const answer = typeof o.answer === 'string' || typeof o.answer === 'number' ? String(o.answer).trim() : '';
  if (!p || !answer) return null;
  const newFig = normalizeFigure(o.figure) ?? figure; // 新圖表壞掉就退回原圖表
  return {
    subject: item.subject as Subject,
    kp: item.kp,
    prompt: p,
    displayAnswer: answer,
    acceptedAnswers: [normalizeAnswer(answer)],
    explanation: typeof o.explanation === 'string' ? o.explanation.trim() : '',
    figure: newFig,
  };
}

/** 為一題考卷題生一題變形題(有圖走帶圖 LLM;否則走 generateSimilarQuestion)。 */
async function variantFor(item: ExtractedItem, seed: number): Promise<VariantItem | null> {
  if (item.figure) return generateFigureVariant(item);
  const sim = await generateSimilarQuestion(item.kp, item.subject, item.prompt, seed);
  if (!sim) return null;
  return {
    subject: item.subject as Subject,
    kp: item.kp,
    prompt: sim.prompt,
    displayAnswer: sim.displayAnswer,
    acceptedAnswers: sim.acceptedAnswers,
    explanation: sim.explanation,
  };
}

/**
 * 同時最多打幾路 LLM。實測(2026-06-11):單機 bridge 一發 ~20s,6 發平行拉到
 * 26-40s,整卷 14 發全平行 → 大半逾時 →「生不出考卷」。3 路是吞吐與穩定的平衡。
 */
const VARIANT_CONCURRENCY = 3;

/**
 * 一發 LLM 打包幾題。一題一發時每題固定吃一次 CLI 冷啟動(~20-30s/題),
 * boss 2026-06-11「為什麼變形生成要這麼久」→ 打包 6 題/發,呼叫數砍到 1/6。
 */
const VARIANT_BATCH = 6;

const SUBJECT_ZH: Record<string, string> = {
  math: '數學', chinese: '國語', english: '英語', nature: '自然', social: '社會',
};

/** 數學且有本地產生器的題:免 LLM、毫秒級。 */
function hasLocalGenerator(item: VariantSourceItem): boolean {
  return item.subject === 'math' && !item.figure && !!getMathGenerator(item.kp);
}

/**
 * 一批(≤VARIANT_BATCH 題)一次 LLM 呼叫生變形。回 Map<原題 index, VariantItem>;
 * 整批失敗回空 Map(呼叫端逐題保底)。
 */
async function generateBatchVariants(
  batch: { item: VariantSourceItem; index: number }[],
): Promise<Map<number, VariantItem>> {
  const out = new Map<number, VariantItem>();
  const list = batch
    .map((b, i) => {
      const subj = SUBJECT_ZH[b.item.subject] ?? b.item.subject;
      const fig = b.item.figure ? `\n   圖表資料(JSON):${JSON.stringify(b.item.figure)}` : '';
      return `${i + 1}. [${subj}] 原題:${b.item.prompt}${fig}`;
    })
    .join('\n');
  const system =
    '你是台灣國小三年級的出題老師。只用繁體中文。只輸出單一 JSON 陣列,不要任何多餘文字或 markdown。';
  const prompt =
    `下面有 ${batch.length} 題學生剛考過的題目。請為**每一題**各出一題「同類型、同難度」的新變形題:` +
    '數字/用詞/情境要換過,不要照抄;每題必須自包含。有「圖表資料」的題,要附上換過數字的新圖表' +
    '(與原圖表同 type、同欄位結構),答案依新數字計算。國語題注意注音聲調與國字正確性,沒把握就換字。\n' +
    `${list}\n\n` +
    '回覆格式(只回 JSON 陣列,i 對應上面的題號;figure 只在原題有圖表資料時才附):\n' +
    '[{"i":1,"prompt":"新題目文字","answer":"標準答案(簡短)","explanation":"一句怎麼算","figure":<新圖表或省略>}]';

  const raw = await textComplete({ system, prompt });
  if (!raw) return out;
  const arr = coerceJsonArray(raw);
  if (!arr) return out;

  for (const entry of arr) {
    if (!entry || typeof entry !== 'object') continue;
    const o = entry as Record<string, unknown>;
    const n = typeof o.i === 'number' ? o.i : parseInt(String(o.i), 10);
    if (!Number.isInteger(n) || n < 1 || n > batch.length) continue;
    const src = batch[n - 1];
    const p = typeof o.prompt === 'string' ? o.prompt.trim() : '';
    const answer = typeof o.answer === 'string' || typeof o.answer === 'number' ? String(o.answer).trim() : '';
    if (!p || !answer) continue;
    const v: VariantItem = {
      subject: src.item.subject as Subject,
      kp: src.item.kp,
      prompt: p,
      displayAnswer: answer,
      acceptedAnswers: [normalizeAnswer(answer)],
      explanation: typeof o.explanation === 'string' ? o.explanation.trim() : '',
    };
    if (src.item.figure) v.figure = normalizeFigure(o.figure) ?? src.item.figure; // 新圖壞掉退回原圖
    out.set(src.index, v);
  }
  return out;
}

/**
 * 為整張卷的題目各生一題變形題:
 *   1) 數學有產生器的題走本地(毫秒級、免 LLM)
 *   2) 其餘打包批次(6 題/發、3 路平行)— 一題一發太慢(boss 2026-06-11)
 *   3) 失敗者逐題重試一輪保底
 * 匯出供測試 / 其他入口重用。
 */
export async function buildVariantItems(
  items: ExtractedItem[],
  onProgress?: (done: number, total: number) => void,
): Promise<VariantItem[]> {
  const out: (VariantItem | null)[] = new Array(items.length).fill(null);
  const seedBase = Date.now() & 0x7fffffff;
  let done = 0;
  const tick = (): void => {
    done++;
    onProgress?.(done, items.length);
  };

  // 1) 本地產生器路徑(也套 3 路池:產生器偶有 fallback 到 LLM 的分支)。
  const genIdx: number[] = [];
  const llmIdx: number[] = [];
  items.forEach((it, i) => (hasLocalGenerator(it) ? genIdx : llmIdx).push(i));
  let g = 0;
  async function genLane(): Promise<void> {
    while (g < genIdx.length) {
      const i = genIdx[g++];
      out[i] = await variantFor(items[i], (seedBase + i * 7919) >>> 0).catch(() => null);
      if (out[i]) tick();
    }
  }
  await Promise.all(Array.from({ length: Math.min(VARIANT_CONCURRENCY, genIdx.length) }, () => genLane()));

  // 2) LLM 批次。
  const chunks: number[][] = [];
  for (let c = 0; c < llmIdx.length; c += VARIANT_BATCH) chunks.push(llmIdx.slice(c, c + VARIANT_BATCH));
  let cursor = 0;
  async function lane(): Promise<void> {
    while (cursor < chunks.length) {
      const chunk = chunks[cursor++];
      const got = await generateBatchVariants(chunk.map((i) => ({ item: items[i], index: i }))).catch(
        () => new Map<number, VariantItem>(),
      );
      for (const i of chunk) {
        const v = got.get(i);
        if (v) {
          out[i] = v;
          tick();
        }
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(VARIANT_CONCURRENCY, chunks.length) }, () => lane()));

  // 3) 失敗者逐題重試一輪(舊 per-item 路徑當保底;再失敗就放掉)。
  const failed = out.map((v, i) => (v ? -1 : i)).filter((i) => i >= 0);
  if (failed.length > 0) {
    let c2 = 0;
    async function retryLane(): Promise<void> {
      while (c2 < failed.length) {
        const i = failed[c2++];
        out[i] = await variantFor(items[i], (seedBase + i * 7919) >>> 0).catch(() => null);
        if (out[i]) tick();
      }
    }
    await Promise.all(Array.from({ length: Math.min(VARIANT_CONCURRENCY, failed.length) }, () => retryLane()));
  }

  return out.filter((v): v is VariantItem => v !== null);
}

/**
 * 🎯 重點挑題(LLM):把整卷按「考的概念」分群,每群挑 1 題代表(答錯 > 粗心 >
 * 較難),答錯/粗心必收,目標 4~6 題。回傳 0-based 題號;解析失敗回 null。
 */
async function pickKeyIndicesLLM(items: VariantSourceItem[]): Promise<number[] | null> {
  const list = items
    .map((it, i) => {
      const mark =
        it.decision === 'wrong' ? '錯' : it.decision === 'careless' ? '粗心' : it.decision === 'correct' ? '對' : '未知';
      return `${i + 1}. [${mark}]${it.figure ? '[圖表題]' : ''} ${it.prompt}`;
    })
    .join('\n');
  const system =
    '你是台灣國小三年級的出題老師。只輸出單一 JSON 物件,不要任何多餘文字或 markdown。';
  const prompt =
    '下面是一張考卷的題目清單(含批改結果)。請把題目按「考的概念」分群,每個概念挑 1 題代表題,' +
    '優先順序:答錯 > 粗心 > 該概念較難的一題。答錯與粗心的題必須入選。總共挑 4~6 題' +
    '(錯題超過 6 題就全收錯題)。\n' +
    `${list}\n\n` +
    '用這個 JSON 格式回覆(只回 JSON,題號用上面的編號):\n{"picks":[1,5,8]}';
  const raw = await textComplete({ system, prompt });
  if (!raw) return null;
  const cleaned = raw.replace(/^```(?:json)?\s*|\s*```$/g, '').trim();
  try {
    const o = JSON.parse(cleaned) as { picks?: unknown };
    if (!Array.isArray(o.picks)) return null;
    const idx = [...new Set(o.picks)]
      .filter((n): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= items.length)
      .map((n) => n - 1);
    return idx.length > 0 ? idx : null;
  } catch {
    return null;
  }
}

/** decision 排序權重:錯最優先、粗心次之、對/未知最後。 */
function decisionRank(d?: VariantDecision): number {
  return d === 'wrong' ? 0 : d === 'careless' ? 1 : 2;
}

/**
 * 重點挑題保底(LLM 不可用時):按 kp 分群,每群挑批改結果最差的一題當代表,
 * 群代表再按差的優先排序,取前 `cap` 題。純函式,匯出供測試。
 */
export function fallbackKeyPick(items: VariantSourceItem[], cap: number = KEY_PICK_THRESHOLD): VariantSourceItem[] {
  const groups = new Map<string, VariantSourceItem[]>();
  for (const it of items) {
    const g = groups.get(it.kp) ?? [];
    g.push(it);
    groups.set(it.kp, g);
  }
  const reps = [...groups.values()].map(
    (g) => [...g].sort((a, b) => decisionRank(a.decision) - decisionRank(b.decision))[0],
  );
  reps.sort((a, b) => decisionRank(a.decision) - decisionRank(b.decision));
  return reps.slice(0, cap);
}

/**
 * 重點短卷的題目來源:LLM 挑(答錯必收的保險絲在本函式 — 就算 LLM 漏了,
 * 答錯/粗心的題一律補進來);LLM 失敗 → fallbackKeyPick。匯出供測試。
 */
export async function selectKeyItems(items: VariantSourceItem[]): Promise<VariantSourceItem[]> {
  const picked = await pickKeyIndicesLLM(items).catch(() => null);
  if (!picked) return fallbackKeyPick(items);
  const idxSet = new Set(picked);
  items.forEach((it, i) => {
    if (it.decision === 'wrong' || it.decision === 'careless') idxSet.add(i);
  });
  return [...idxSet].sort((a, b) => a - b).map((i) => items[i]);
}

/** 家長批改三選一(沿用出練習卷):對 / 粗心 / 不會。 */
export type VariantMark = 'correct' | 'careless' | 'wrong';

/**
 * 依批改結果入帳:對→升盒;粗心→不降盒 + 錯題本(標粗心);不會→降盒 + 錯題本。
 * 只動學習側。失敗不阻斷 UI。
 */
function gradeVariantMark(item: VariantItem, mark: VariantMark): void {
  try {
    const progress = loadProgress();
    if (!progress) return;
    const nowISO = new Date().toISOString();
    const kp = findOrCreateKP(progress, item.kp, item.subject);
    const qid = deriveQuestionId(item.prompt);
    recordAttemptV2(progress, kp, mark, nowISO, qid);
    if (mark === 'wrong' || mark === 'careless') {
      recordMistake(progress, {
        questionId: qid,
        kp: item.kp,
        subject: item.subject,
        prompt: item.prompt,
        correctAnswer: item.displayAnswer,
        userAnswer: null,
        explanation: item.explanation || undefined,
      });
      if (mark === 'careless') {
        const entry = progress.mistakes.find((m) => m.questionId === qid);
        if (entry) entry.errorType = 'careless';
      }
      // 錯題回流學生 LLM Wiki(R-001;best-effort,不阻斷)。
      feedMistakesToWiki(progress, [
        { subject: item.subject, kp: item.kp, prompt: item.prompt, correctAnswer: item.displayAnswer },
      ]);
    }
    saveProgress(progress);
  } catch {
    /* 入帳失敗不影響作答 UI */
  }
}

/** 線上作答用的二元包裝(對/錯)。 */
function gradeVariant(item: VariantItem, correct: boolean): void {
  gradeVariantMark(item, correct ? 'correct' : 'wrong');
}

/**
 * 「出變形練習卷」入口(boss 2026-06-10「可以」拍板雙模式):
 *   題數 ≤ KEY_PICK_THRESHOLD → 直接整卷變形(挑重點沒意義)。
 *   題數多 → 先選「🎯 重點短卷(預設推薦)/ 📋 整卷變形」。
 * onDone 在離開時呼叫。
 */
export async function runVariantPractice(
  container: HTMLElement,
  items: VariantSourceItem[],
  onDone: () => void,
): Promise<void> {
  const shell = pageShell(container, {
    title: '變形練習卷',
    subtitle: '用這張考卷出換過數字/情境的新題 — 印成 A4 用筆寫,寫完家長對答案。',
    onBack: onDone,
  });

  if (items.length <= KEY_PICK_THRESHOLD) {
    await buildAndRender(shell.body, items, onDone);
    return;
  }

  // ── 模式選擇(學術風,中性文案)──
  const choice = document.createElement('div');
  choice.className = 'vp-mode-choice';
  choice.setAttribute('data-testid', 'variant-mode-choice');
  choice.style.cssText = 'display:flex;flex-direction:column;gap:10px;';

  const desc = document.createElement('p');
  desc.style.cssText = 'margin:0;font-size:14px;line-height:1.7;color:var(--ef-sub);';
  desc.textContent =
    `這張卷共 ${items.length} 題。重點短卷會按概念分群、每個概念挑 1 題代表(答錯的必收)再變形,` +
    '約 4~6 題,短而準;整卷變形則每一題都出一題新的。';
  choice.appendChild(desc);

  const keyBtn = academicButton({
    label: '🎯 重點短卷(推薦)',
    variant: 'primary',
    onClick: () => {
      choice.remove();
      void (async () => {
        const picking = document.createElement('p');
        picking.textContent = '正在挑這張卷的重點題…';
        picking.style.cssText = 'font-size:15px;color:var(--ef-sub);line-height:1.7;';
        shell.body.appendChild(picking);
        const picked = await selectKeyItems(items);
        picking.remove();
        await buildAndRender(shell.body, picked, onDone);
      })();
    },
  });
  keyBtn.setAttribute('data-testid', 'variant-mode-key');
  choice.appendChild(keyBtn);

  const fullBtn = academicButton({
    label: `📋 整卷變形(${items.length} 題)`,
    variant: 'secondary',
    onClick: () => {
      choice.remove();
      void buildAndRender(shell.body, items, onDone);
    },
  });
  fullBtn.setAttribute('data-testid', 'variant-mode-full');
  choice.appendChild(fullBtn);

  shell.body.appendChild(choice);
}

/** 生變形題 → 考卷視圖(生不出顯示「再試一次」)。 */
async function buildAndRender(body: HTMLElement, items: VariantSourceItem[], onDone: () => void): Promise<void> {
  const loading = document.createElement('p');
  loading.textContent = `正在出 ${items.length} 題變形題…(打包生成,通常一兩分鐘內)`;
  loading.style.cssText = 'font-size:15px;color:var(--ef-sub);line-height:1.7;';
  body.appendChild(loading);

  const variants = await buildVariantItems(items, (done, total) => {
    loading.textContent = `正在出變形題… 已完成 ${done} / ${total} 題`;
  });
  loading.remove();

  if (variants.length === 0) {
    const empty = document.createElement('p');
    empty.textContent = '這次沒能生出變形題(可能後端忙線)。';
    empty.style.cssText = 'font-size:15px;color:var(--ef-sub);';
    body.appendChild(empty);
    const retry = academicButton({
      label: '🔄 再試一次',
      variant: 'primary',
      onClick: () => {
        body.innerHTML = '';
        void buildAndRender(body, items, onDone);
      },
    });
    retry.setAttribute('data-testid', 'variant-retry');
    body.appendChild(retry);
    const back = academicButton({ label: '← 返回', variant: 'secondary', onClick: onDone });
    body.appendChild(back);
    return;
  }

  // 預設 = 考卷視圖(boss 2026-06-11「我不要線上的題目」+ 鐵則「輸出永遠考卷形式」);
  // 線上作答收為次要選項。
  renderVariantSheet(body, variants, onDone);
}

/* ─────────────────────────────────────────────────────────────────────────
   考卷視圖(預設)— 沿用出練習卷的版面語彙:卷頭 + 連續題號 + 作答格 + 🖨️A4
   ───────────────────────────────────────────────────────────────────────── */

function renderVariantSheet(body: HTMLElement, variants: VariantItem[], onDone: () => void): void {
  ensureVariantStyle();
  body.innerHTML = '';

  const root = document.createElement('div');
  root.className = 'variant-sheet';
  root.setAttribute('data-testid', 'variant-sheet');
  body.appendChild(root);

  // 按鈕列(同出練習卷):列印 secondary、寫完了對答案 primary;線上作答留作次要。
  const toolbar = document.createElement('div');
  toolbar.className = 'vs-toolbar';
  const printBtn = academicButton({
    label: '🖨️ 列印(A4)',
    variant: 'secondary',
    onClick: () => printVariantPaper(variants),
  });
  printBtn.setAttribute('data-testid', 'variant-print');
  const gradeBtn = academicButton({
    label: '✅ 寫完了,對答案',
    variant: 'primary',
    onClick: () => renderVariantGrade(body, variants, onDone),
  });
  gradeBtn.setAttribute('data-testid', 'variant-grade-btn');
  const onlineBtn = academicButton({
    label: '✍️ 改用線上作答',
    variant: 'secondary',
    onClick: () => {
      body.innerHTML = '';
      renderVariantPaper(body, variants, onDone);
    },
  });
  onlineBtn.setAttribute('data-testid', 'variant-online');
  toolbar.append(printBtn, gradeBtn, onlineBtn);
  root.appendChild(toolbar);

  // 卷頭(像考卷:標題 + 姓名/日期/得分)。
  const header = document.createElement('div');
  header.className = 'vs-header';
  header.innerHTML =
    '<div class="vs-title">變形練習卷</div>' +
    '<div class="vs-meta">' +
    '<span>姓名:<span class="vs-blank" style="min-width:5em"></span></span>' +
    '<span>日期:<span class="vs-blank" style="min-width:5em"></span></span>' +
    '<span>得分:<span class="vs-blank" style="min-width:3em"></span></span>' +
    '</div>';
  root.appendChild(header);

  variants.forEach((item, idx) => {
    const q = document.createElement('div');
    q.className = 'vs-q';
    const prompt = document.createElement('div');
    prompt.className = 'vs-prompt';
    prompt.textContent = `${idx + 1}. ${item.prompt}`;
    q.appendChild(prompt);
    if (item.figure) {
      try {
        q.appendChild(renderFigure(item.figure));
      } catch {
        /* 圖壞不擋題 */
      }
    }
    const ans = document.createElement('div');
    ans.className = 'vs-answer';
    ans.innerHTML = '答:<span class="vs-blank" style="min-width:8em"></span>';
    q.appendChild(ans);
    root.appendChild(q);
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   🖨️ A4 列印(沿用 practice-flow 的 window.open + window.print 模式)
   ───────────────────────────────────────────────────────────────────────── */

/** 產生列印用 HTML(可獨立測試)。圖表用 renderFigure 的 DOM + 同一份 CSS。 */
export function buildVariantPrintHtml(variants: VariantItem[]): string {
  const qs = variants
    .map((item, i) => {
      let fig = '';
      if (item.figure) {
        try {
          fig = renderFigure(item.figure).outerHTML; // 同時保證 ef-figure-style 已注入主文件
        } catch {
          /* 圖壞不擋題 */
        }
      }
      return `<div class="q">
        <div class="prompt">${i + 1}. ${escapeHtml(item.prompt)}</div>
        ${fig}
        <div class="answer-line">答:<span class="blank">&nbsp;</span></div>
      </div>`;
    })
    .join('');

  // 圖表樣式吃 --ef-* 變數;列印視窗沒有主題,給一組黑白友善的 fallback。
  const figCss =
    (typeof document !== 'undefined' && document.getElementById('ef-figure-style')?.textContent) || '';
  const printVars =
    ':root{--ef-serif:"Songti TC","STSong","SimSun","PMingLiU",serif;--ef-ink:#000;' +
    '--ef-ink-soft:#222;--ef-line:#888;--ef-paper:#fff;--ef-paper-bright:#fff;' +
    '--ef-green-dark:#1f6f5c;--ef-radius:6px;--ef-radius-sm:4px;--ef-sub:#555;}';

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>變形練習卷</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body { font-family: "Songti TC", "STSong", "SimSun", "PMingLiU", serif; color: #000; line-height: 1.6; }
  ${printVars}
  ${figCss}
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: bold; text-align: center; }
  .meta { font-size: 12px; margin-top: 8px; display: flex; flex-wrap: wrap; gap: 16px; }
  .meta .blank, .answer-line .blank { display: inline-block; min-width: 5em; border-bottom: 1px solid #000; padding: 0 4px; }
  .q { margin-bottom: 16px; page-break-inside: avoid; }
  .prompt { font-size: 14px; margin-bottom: 6px; white-space: pre-wrap; }
  .answer-line { font-size: 13px; }
  .answer-line .blank { min-width: 8em; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">變形練習卷</div>
  <div class="meta">
    <span>姓名:<span class="blank">&nbsp;</span></span>
    <span>日期:<span class="blank">&nbsp;</span></span>
    <span>得分:<span class="blank">&nbsp;</span></span>
  </div>
</div>
${qs}
</body>
</html>`;
}

function printVariantPaper(variants: VariantItem[]): void {
  const html = buildVariantPrintHtml(variants);
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}

/* ─────────────────────────────────────────────────────────────────────────
   家長批改(逐題正解 + 對/粗心/不會 三鈕,同出練習卷)
   ───────────────────────────────────────────────────────────────────────── */

function renderVariantGrade(body: HTMLElement, variants: VariantItem[], onDone: () => void): void {
  ensureVariantStyle();
  body.innerHTML = '';

  const root = document.createElement('div');
  root.className = 'variant-grade';
  root.setAttribute('data-testid', 'variant-grade-screen');
  body.appendChild(root);

  const heading = document.createElement('h2');
  heading.className = 'vg-title';
  heading.textContent = '家長批改 — 對答案';
  root.appendChild(heading);

  const hint = document.createElement('p');
  hint.className = 'vg-hint';
  hint.textContent = '逐題看正解,幫孩子標記:【對】/【粗心】(算對但寫錯)/【不會】(觀念不懂)。沒寫的題可以不標。';
  root.appendChild(hint);

  const marks: (VariantMark | null)[] = variants.map(() => null);

  variants.forEach((item, idx) => {
    const card = document.createElement('div');
    card.className = 'vg-card';

    const prompt = document.createElement('div');
    prompt.className = 'vg-prompt';
    prompt.textContent = `${idx + 1}. ${item.prompt}`;
    card.appendChild(prompt);

    if (item.figure) {
      try {
        card.appendChild(renderFigure(item.figure));
      } catch {
        /* 圖壞不擋題 */
      }
    }

    const answer = document.createElement('div');
    answer.className = 'vg-answer';
    const lines = [`正解:${item.displayAnswer}`];
    if (item.explanation) lines.push(`解析:${item.explanation}`);
    answer.textContent = lines.join('\n');
    card.appendChild(answer);

    const row = document.createElement('div');
    row.className = 'vg-marks';
    const options: { mark: VariantMark; label: string; variant: 'primary' | 'secondary' | 'danger' }[] = [
      { mark: 'correct', label: '對', variant: 'primary' },
      { mark: 'careless', label: '粗心', variant: 'secondary' },
      { mark: 'wrong', label: '不會', variant: 'danger' },
    ];
    const buttons: HTMLButtonElement[] = [];
    for (const opt of options) {
      const b = academicButton({ label: opt.label, variant: opt.variant, onClick: () => {} });
      b.dataset.mark = opt.mark;
      b.addEventListener('click', () => {
        marks[idx] = opt.mark;
        buttons.forEach((bb) => {
          const on = bb === b;
          bb.style.outline = on ? '2px solid var(--ef-ink)' : '';
          bb.style.outlineOffset = on ? '2px' : '';
        });
      });
      buttons.push(b);
      row.appendChild(b);
    }
    card.appendChild(row);
    root.appendChild(card);
  });

  const finish = academicButton({
    label: '完成批改',
    variant: 'primary',
    onClick: () => {
      variants.forEach((item, idx) => {
        const mark = marks[idx];
        if (mark) gradeVariantMark(item, mark);
      });
      renderVariantSummary(body, marks, onDone);
    },
  });
  finish.setAttribute('data-testid', 'variant-grade-finish');
  root.appendChild(finish);
}

/** 批改完的摘要:對幾題、錯/粗心幾題進錯題本。 */
function renderVariantSummary(body: HTMLElement, marks: (VariantMark | null)[], onDone: () => void): void {
  body.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'variant-grade';
  root.setAttribute('data-testid', 'variant-summary');
  body.appendChild(root);

  const heading = document.createElement('h2');
  heading.className = 'vg-title';
  heading.textContent = '批改完成';
  root.appendChild(heading);

  const graded = marks.filter((m) => m !== null).length;
  const correct = marks.filter((m) => m === 'correct').length;
  const toMistake = graded - correct;

  const summary = document.createElement('div');
  summary.className = 'vg-summary';
  const lines = [`這次對 ${correct} / ${graded} 題。`];
  if (toMistake > 0) lines.push(`${toMistake} 題(不會/粗心)已加進錯題本,下次再練。`);
  if (graded < marks.length) lines.push(`${marks.length - graded} 題沒批改,不計。`);
  summary.textContent = lines.join('\n');
  summary.style.whiteSpace = 'pre-wrap';
  root.appendChild(summary);

  const done = academicButton({ label: '完成,返回', variant: 'primary', onClick: onDone });
  done.style.marginTop = '12px';
  root.appendChild(done);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** 逐題作答畫面(填答 + 對答案 + 顯示正解 + 圖表)。 */
function renderVariantPaper(body: HTMLElement, variants: VariantItem[], onDone: () => void): void {
  ensureVariantStyle();
  const root = document.createElement('div');
  root.className = 'variant-practice';
  body.appendChild(root);

  const head = document.createElement('p');
  head.className = 'vp-meta';
  head.textContent = `共 ${variants.length} 題,寫好按「對答案」。答錯的會自動進錯題本下次再練。`;
  root.appendChild(head);

  const graded: Array<{ correct: boolean } | null> = new Array(variants.length).fill(null);

  variants.forEach((item, idx) => {
    const card = document.createElement('div');
    card.className = 'vp-item ef-card';

    const num = document.createElement('div');
    num.className = 'vp-num';
    num.textContent = `第 ${idx + 1} 題`;
    card.appendChild(num);

    const prompt = document.createElement('div');
    prompt.className = 'vp-prompt';
    prompt.textContent = item.prompt;
    card.appendChild(prompt);

    if (item.figure) {
      try { card.appendChild(renderFigure(item.figure)); } catch { /* 圖失敗不影響題 */ }
    }

    const row = document.createElement('div');
    row.className = 'vp-row';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'vp-input';
    input.placeholder = '輸入答案';
    input.setAttribute('aria-label', `第 ${idx + 1} 題答案`);
    const checkBtn = academicButton({ label: '對答案', variant: 'primary', onClick: () => check() });
    checkBtn.classList.add('vp-check');
    row.append(input, checkBtn);
    card.appendChild(row);

    const feedback = document.createElement('div');
    feedback.className = 'vp-feedback';
    card.appendChild(feedback);

    const reveal = document.createElement('div');
    reveal.className = 'vp-reveal';
    reveal.hidden = true;
    reveal.textContent = `正解:${item.displayAnswer}${item.explanation ? ` —— ${item.explanation}` : ''}`;
    card.appendChild(reveal);

    const check = (): void => {
      if (graded[idx]) return;
      const got = normalizeAnswer(input.value);
      if (got === '') { input.focus(); return; }
      const correct = item.acceptedAnswers.includes(got);
      graded[idx] = { correct };
      gradeVariant(item, correct);
      feedback.className = 'vp-feedback ' + (correct ? 'vp-correct' : 'vp-wrong');
      feedback.textContent = correct ? '✓ 答對了!' : '✗ 看一下正解,已加進錯題本下次再練。';
      reveal.hidden = false;
      input.disabled = true;
      (checkBtn as HTMLButtonElement).disabled = true;
      updateSummary();
    };
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') check(); });

    root.appendChild(card);
  });

  const footer = document.createElement('div');
  footer.className = 'vp-footer';
  const summary = document.createElement('p');
  summary.className = 'vp-summary';
  footer.appendChild(summary);
  const back = academicButton({ label: '← 完成,返回', variant: 'secondary', onClick: onDone });
  footer.appendChild(back);
  root.appendChild(footer);

  function updateSummary(): void {
    const done = graded.filter((g) => g !== null).length;
    const right = graded.filter((g) => g?.correct).length;
    summary.textContent = done === 0 ? '' : `已對答 ${done}/${variants.length} 題,答對 ${right} 題。`;
  }
}

const VARIANT_STYLE_ID = 'ef-variant-practice-style';
function ensureVariantStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(VARIANT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = VARIANT_STYLE_ID;
  style.textContent = `
.variant-practice { display:flex; flex-direction:column; gap:14px; font-family:var(--ef-sans); color:var(--ef-ink-soft); }
.variant-practice .vp-meta { margin:0 0 4px; font-size:13px; color:var(--ef-sub); line-height:1.6; }
.variant-practice .vp-item { display:flex; flex-direction:column; gap:10px; cursor:default; }
.variant-practice .vp-num { font-size:12px; font-weight:600; color:var(--ef-sub); letter-spacing:0.5px; }
.variant-practice .vp-prompt { font-family:var(--ef-serif); font-size:17px; line-height:1.55; color:var(--ef-ink); }
.variant-practice .vp-row { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.variant-practice .vp-input { flex:1 1 160px; min-height:44px; padding:10px 14px; border:1px solid var(--ef-line); border-radius:var(--ef-radius-sm); background:#fff; font-size:16px; color:var(--ef-ink); font-family:inherit; }
.variant-practice .vp-input:focus-visible { outline:3px solid rgba(31,111,92,0.35); outline-offset:1px; border-color:var(--ef-green); }
.variant-practice .vp-input:disabled { background:var(--ef-paper); color:var(--ef-sub); }
.variant-practice .vp-feedback { font-size:14px; min-height:1px; }
.variant-practice .vp-feedback.vp-correct { color:var(--ef-green-dark); font-weight:600; }
.variant-practice .vp-feedback.vp-wrong { color:#b3261e; }
.variant-practice .vp-reveal { font-size:14px; line-height:1.6; color:var(--ef-ink-soft); padding:10px 12px; background:var(--ef-note,#f3f1e7); border-radius:var(--ef-radius-sm); }
.variant-practice .vp-footer { display:flex; flex-direction:column; gap:10px; padding-top:12px; border-top:1px solid var(--ef-line); }
.variant-practice .vp-summary { margin:0; font-size:14px; color:var(--ef-ink-soft); font-weight:600; }
/* 考卷視圖(預設):像一張白卷面 */
.variant-sheet { font-family:var(--ef-serif); color:var(--ef-ink-soft); background:var(--ef-paper-bright); border:1px solid var(--ef-line); border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card); padding:22px 24px 36px; line-height:1.7; }
.variant-sheet .vs-toolbar { display:flex; gap:10px; flex-wrap:wrap; margin:0 0 18px; }
.variant-sheet .vs-header { border-bottom:2px solid var(--ef-ink); padding-bottom:10px; margin-bottom:18px; }
.variant-sheet .vs-title { font-family:var(--ef-serif); font-size:21px; font-weight:700; letter-spacing:1px; text-align:center; color:var(--ef-ink); margin-bottom:8px; }
.variant-sheet .vs-meta { display:flex; flex-wrap:wrap; gap:16px; font-size:14px; color:var(--ef-ink-soft); }
.variant-sheet .vs-blank { display:inline-block; border-bottom:1px solid var(--ef-ink); }
.variant-sheet .vs-q { margin:0 0 16px; }
.variant-sheet .vs-prompt { font-size:15px; color:var(--ef-ink); margin-bottom:6px; white-space:pre-wrap; }
.variant-sheet .vs-answer { font-size:14px; color:var(--ef-ink); }
/* 家長批改 */
.variant-grade { display:flex; flex-direction:column; gap:12px; font-family:var(--ef-sans); color:var(--ef-ink-soft); }
.variant-grade .vg-title { font-family:var(--ef-serif); font-size:20px; font-weight:700; color:var(--ef-ink); margin:0; }
.variant-grade .vg-hint { font-size:14px; color:var(--ef-sub); margin:0 0 4px; line-height:1.6; }
.variant-grade .vg-card { border:1px solid var(--ef-line); border-radius:var(--ef-radius-sm); padding:14px 16px; background:var(--ef-paper); }
.variant-grade .vg-prompt { font-size:15px; color:var(--ef-ink); margin-bottom:6px; white-space:pre-wrap; }
.variant-grade .vg-answer { font-size:14px; color:#b3261e; border-left:3px solid #b3261e; padding-left:8px; margin:8px 0 12px; white-space:pre-wrap; }
.variant-grade .vg-marks { display:flex; gap:10px; flex-wrap:wrap; }
.variant-grade .vg-summary { font-size:15px; color:var(--ef-ink); line-height:1.8; }
`;
  document.head.appendChild(style);
}
